<template>
  <div class="organization-details-page">
    <div class="container message-wrapper">
      <button class="back-button" @click="goBack">
        <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
        <span>Back to Organizations</span>
      </button>

      <div v-if="organization" class="details-shell">
        <section class="details-hero">
          <div class="hero-main">
            <p class="hero-eyebrow">Organization Profile</p>
            <h1>{{ organization.name }}</h1>
            <p class="hero-subtitle">
              Comprehensive overview of organization identity, compliance documents, staffing,
              collaborations, and operational records.
            </p>

            <div class="hero-meta">
              <span
                class="hero-pill"
                :class="
                  organization.status === 'Approved' ? 'hero-pill--success' : 'hero-pill--pending'
                "
              >
                {{ organization.status || 'Status unavailable' }}
              </span>
              <span class="hero-meta-text">{{ organization.state || 'No state' }}</span>
              <span class="hero-meta-text">{{ organization.geo_zone || 'No geo zone' }}</span>
            </div>
          </div>

          <div class="hero-stats">
            <div class="hero-stat">
              <span class="hero-stat__label">Areas of focus</span>
              <strong class="hero-stat__value">{{
                organization.areas_of_focus ? organization.areas_of_focus.length : 0
              }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Contacts</span>
              <strong class="hero-stat__value">{{
                organization.contact_persons ? organization.contact_persons.length : 0
              }}</strong>
            </div>
            <div class="hero-stat">
              <span class="hero-stat__label">Last updated</span>
              <strong class="hero-stat__value hero-stat__value--small">{{
                formatDisplayDate(organization.updated_on)
              }}</strong>
            </div>
          </div>
        </section>

        <section class="section-nav">
          <div class="section-nav__copy">
            <p class="section-kicker">Jump to section</p>
            <h3>Move through the organization record faster</h3>
          </div>
          <div class="section-nav__chips">
            <a
              v-for="item in sectionLinks"
              :key="item.id"
              class="section-chip"
              :href="`#section-${item.id}`"
            >
              {{ item.label }}
            </a>
          </div>
        </section>

        <div class="details-card">
          <div class="details-card__header">
            <div>
              <p class="section-kicker">Overview</p>
              <h2>Organization Detail Overview</h2>
            </div>
            <p class="details-card__note">
              Review each section carefully before publishing any saved updates.
            </p>
          </div>

          <div class="card-body premium-card-body">
            <!-- GENERAL INFORMATION -->
            <SectionWrapper
              title="General Information"
              section="general"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="General Email">
                    <template v-if="editSection === 'general'">
                      <input v-model="tempData.email" type="email" class="form-control" />
                    </template>
                    <template v-else>{{ organization.email || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Web URL">
                    <template v-if="editSection === 'general'">
                      <input v-model="tempData.web_url" type="url" class="form-control" />
                    </template>
                    <template v-else>{{ organization.web_url || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Phone Number">
                    <template v-if="editSection === 'general'">
                      <input v-model="tempData.phone_number" type="text" class="form-control" />
                    </template>
                    <template v-else>{{ organization.phone_number || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Fax Number">
                    <template v-if="editSection === 'general'">
                      <input v-model="tempData.fax_number" type="text" class="form-control" />
                    </template>
                    <template v-else>{{ organization.fax_number || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>

                <div class="col-md-6">
                  <InfoBlock label="Geo Zone">
                    <template v-if="editSection === 'general'">
                      <select v-model="selectedZone" class="form-select w-100 py-2">
                        <option :value="null">Select Geo Zone</option>
                        <option v-for="zone in location" :key="zone.name" :value="zone">
                          {{ zone.name }}
                        </option>
                      </select>
                    </template>
                    <template v-else>{{ organization.geo_zone || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="State">
                    <template v-if="editSection === 'general'">
                      <select v-model="tempData.state" class="form-select w-100 py-2">
                        <option value="">Select State</option>
                        <option v-for="s in selectedZone.states" :key="s" :value="s">
                          {{ s }}
                        </option>
                      </select>
                    </template>
                    <template v-else>{{ organization.state || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Address">
                    <template v-if="editSection === 'general'">
                      <textarea v-model="tempData.address" class="form-control"></textarea>
                    </template>
                    <template v-else>{{ organization.address || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- CONTACT PERSONS -->
            <SectionWrapper
              title="Contact Persons"
              section="contact"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div v-if="activeContacts.length === 0" class="text-muted fst-italic p-2">
                No contact persons available
                <div v-if="editSection === 'contact'" class="mt-2">
                  <button @click="addContact" class="btn btn-primary w-25 rounded">
                    Add Contact
                  </button>
                </div>
              </div>

              <div v-else class="row my-3">
                <div
                  v-for="(person, index) in activeContacts"
                  :key="person.id || index"
                  class="col-md-6 mb-4 border g-1"
                >
                  <label>Contact Person {{ index + 1 }}</label>

                  <InfoBlock label="Full Name">
                    <template v-if="editSection === 'contact'">
                      <input v-model="person.full_name" class="form-control" />
                    </template>
                    <template v-else>{{ person.full_name || '-' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Position">
                    <template v-if="editSection === 'contact'">
                      <input v-model="person.position" class="form-control" />
                    </template>
                    <template v-else>{{ person.position || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Email">
                    <template v-if="editSection === 'contact'">
                      <input v-model="person.email" type="email" class="form-control" />
                    </template>
                    <template v-else>{{ person.email || '-' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Phone">
                    <template v-if="editSection === 'contact'">
                      <input v-model="person.phone_number" class="form-control" />
                    </template>
                    <template v-else>{{ person.phone_number || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Address">
                    <template v-if="editSection === 'contact'">
                      <textarea v-model="person.address" class="form-control"></textarea>
                    </template>
                    <template v-else>{{ person.address || 'Not Provided' }}</template>
                  </InfoBlock>

                  <div
                    v-if="editSection === 'contact' && activeContacts.length > 1"
                    class="mt-2 d-flex justify-content-end"
                  >
                    <button @click="removeContact(index)" class="btn btn-danger w-50 rounded">
                      Remove Contact
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="editSection === 'contact'" class="mt-2">
                <button @click="addContact" class="btn btn-primary w-25 rounded">
                  Add Contact
                </button>
              </div>
            </SectionWrapper>

            <!-- BASIC INFORMATION -->
            <SectionWrapper
              title="Basic Information"
              section="basic"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="Type">
                    <template v-if="editSection === 'basic'">
                      <input v-model="tempData.type" class="form-control" />
                    </template>
                    <template v-else>{{ organization.type || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Establishment Year">
                    <template v-if="editSection === 'basic'">
                      <input
                        v-model.number="tempData.establishment_year"
                        type="number"
                        class="form-control"
                      />
                    </template>
                    <template v-else>{{
                      organization.establishment_year || 'Not Provided'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="Approval Status">
                    <template>{{ organization.status || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Subscription Status">
                    <template>N/A</template>
                  </InfoBlock>

                  <InfoBlock label="Active">
                    <template v-if="editSection === 'basic'">
                      <select v-model="tempData.active" class="form-select w-25 py-2">
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                      </select>
                    </template>
                    <template v-else>{{ organization.active ? 'Yes' : 'No' }}</template>
                  </InfoBlock>

                  <InfoBlock label="User Email">
                    <template v-if="editSection === 'basic'">
                      <input v-model="tempData.user.email" type="email" class="form-control" />
                    </template>
                    <template v-else>{{ organization.user?.email || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>

                <div class="col-md-6">
                  <InfoBlock label="Umbrella Organization">
                    <template v-if="editSection === 'basic'">
                      <textarea
                        v-model="tempData.umbrella_org"
                        class="form-control"
                        rows="4"
                        placeholder="Enter umbrella organization"
                      ></textarea>
                    </template>
                    <template v-else>
                      <div class="large-text-display">
                        {{ formatListDisplay(organization.umbrella_org) }}
                      </div>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Is Umbrella">
                    <template v-if="editSection === 'basic'">
                      <select
                        v-model="tempData.is_umbrella_organization"
                        class="form-select w-25 py-2"
                      >
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                      </select>
                    </template>
                    <template v-else>{{
                      organization.is_umbrella_organization ? 'Yes' : 'No'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="Organization Focus">
                    <template v-if="editSection === 'basic'">
                      <input v-model="tempData.organization_focus" class="form-control" />
                    </template>
                    <template v-else>{{
                      organization.organization_focus || 'Not Provided'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="Scope of Coverage">
                    <template v-if="editSection === 'basic'">
                      <input v-model="tempData.scope_of_coverage" class="form-control" />
                    </template>
                    <template v-else>{{
                      organization.scope_of_coverage || 'Not Provided'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="Scope">
                    <template v-if="editSection === 'basic'">
                      <input v-model="tempData.scope" class="form-control" />
                    </template>
                    <template v-else>{{ organization.scope || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- AREAS OF FOCUS -->
            <SectionWrapper
              title="Areas of Focus"
              section="focus"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <InfoBlock label="Areas of Focus">
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(focus, index) in editSection === 'focus'
                      ? tempData.areas_of_focus
                      : organization.areas_of_focus"
                    :key="focus.id || index"
                    @click="editSection === 'focus' && removeFocus(index)"
                    class="tags"
                  >
                    {{ focus.name }}
                    <span v-if="editSection === 'focus'" class="remove-btn">×</span>
                  </span>
                  <button
                    v-if="editSection === 'focus'"
                    class="tag-button"
                    type="button"
                    @click="toggleAddFocus"
                  >
                    +
                  </button>
                </div>

                <div v-if="editSection === 'focus' && showAddFocus" class="mt-2">
                  <multiselect
                    v-model="tempArea"
                    tag-placeholder="Add this as new tag"
                    placeholder="Select area(s) of focus"
                    label="name"
                    track-by="id"
                    :options="programs"
                    :multiple="true"
                    :taggable="true"
                  ></multiselect>
                  <div class="d-flex mt-2">
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm"
                      @click="showAddFocus = false"
                    >
                      Cancel
                    </button>
                    <button type="button" class="btn btn-primary btn-sm ml-2" @click="addFocus">
                      Add
                    </button>
                  </div>
                </div>
              </InfoBlock>
            </SectionWrapper>

            <!-- CAC INFORMATION -->
            <SectionWrapper
              title="CAC Information"
              section="cac"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="CAC Registration Year">
                    <template v-if="editSection === 'cac'">
                      <input
                        v-model.number="tempData.cac_registration_year"
                        type="number"
                        class="form-control"
                      />
                    </template>
                    <template v-else>{{
                      organization.cac_registration_year || 'Not Provided'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="CAC Number">
                    <template v-if="editSection === 'cac'">
                      <input v-model="tempData.cac_registration_number" class="form-control" />
                    </template>
                    <template v-else>{{
                      organization.cac_registration_number || 'Not Provided'
                    }}</template>
                  </InfoBlock>

                  <InfoBlock label="CAC Type">
                    <template v-if="editSection === 'cac'">
                      <input v-model="tempData.cac_type" class="form-control" />
                    </template>
                    <template v-else>{{ organization.cac_type || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>

                <div class="col-md-6">
                  <InfoBlock label="CAC Postal Address">
                    <template v-if="editSection === 'cac'">
                      <textarea
                        v-model="tempData.cac_postal_address"
                        class="form-control"
                      ></textarea>
                    </template>
                    <template v-else>{{
                      organization.cac_postal_address || 'Not Provided'
                    }}</template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- BANKING & FUNDING (VIEW) -->
            <SectionWrapper
              title="Banking & Funding"
              section="banking"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              :showEdit="false"
            >
              <InfoBlock label="Bankers">
                <template>
                  <div v-if="organization.bankers && organization.bankers.length">
                    <ul>
                      <li v-for="b in organization.bankers" :key="b.id">
                        <strong>{{ b.name }}</strong> — {{ b.address }}
                      </li>
                    </ul>
                  </div>
                  <div v-else>Not Provided</div>
                </template>
              </InfoBlock>

              <InfoBlock label="Sources of Funding">
                <template>
                  <div
                    v-if="organization.sources_of_funding && organization.sources_of_funding.length"
                  >
                    <ul>
                      <li v-for="f in organization.sources_of_funding" :key="f.id">
                        {{ f.source_name }} — {{ f.description }}
                      </li>
                    </ul>
                  </div>
                  <div v-else>Not Provided</div>
                </template>
              </InfoBlock>

              <InfoBlock label="Funding List (Other)">
                <template>{{ organization.funding_list || 'Not Provided' }}</template>
              </InfoBlock>
            </SectionWrapper>

            <!-- PROJECTS, PUBLICATIONS, RESOURCES, FACILITIES (VIEW) -->
            <SectionWrapper
              title="Projects & Outputs"
              section="projects"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              :showEdit="false"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="Past Projects">
                    <template>
                      <div v-if="organization.past_projects && organization.past_projects.length">
                        <ul>
                          <li v-for="p in organization.past_projects" :key="p.id">
                            <strong>{{ p.title }}</strong> — {{ p.funder }} ({{ p.duration }}) —
                            {{ formatCurrency(p.amount) }}
                          </li>
                        </ul>
                      </div>
                      <div v-else>Not Provided</div>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Publications">
                    <template>
                      <div v-if="organization.publications && organization.publications.length">
                        <ul>
                          <li v-for="pub in organization.publications" :key="pub.id">
                            {{ pub.name }} —
                            <span v-if="pub.sample_url">
                              <a :href="pub.sample_url" target="_blank" download>Download Sample</a>
                            </span>
                            <span v-else>Sample: Not Provided</span>
                          </li>
                        </ul>
                      </div>
                      <div v-else>Not Provided</div>
                    </template>
                  </InfoBlock>
                </div>

                <div class="col-md-6">
                  <InfoBlock label="Resource Persons">
                    <template>
                      <div
                        v-if="organization.resource_persons && organization.resource_persons.length"
                      >
                        <ul>
                          <li v-for="rp in organization.resource_persons" :key="rp.id">
                            {{ rp.name }} —
                            <span v-if="rp.cv_url">
                              <a :href="rp.cv_url" target="_blank" download>Download CV</a>
                            </span>
                            <span v-else>CV: Not Provided</span>
                          </li>
                        </ul>
                      </div>
                      <div v-else>Not Provided</div>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Physical Facilities">
                    <template>
                      <div
                        v-if="
                          organization.physical_facilities &&
                          organization.physical_facilities.length
                        "
                      >
                        <ul>
                          <li v-for="f in organization.physical_facilities" :key="f.facility_name">
                            {{ f.facility_name }} — {{ f.facility_number }}
                          </li>
                        </ul>
                      </div>
                      <div v-else>Not Provided</div>
                    </template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- ASSETS (VIEW) -->
            <SectionWrapper
              title="Assets"
              section="assets"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              :showEdit="false"
            >
              <InfoBlock label="Assets">
                <template>
                  <div v-if="organization.assets && organization.assets.length">
                    <ul>
                      <li v-for="asset in organization.assets" :key="asset.id">
                        <strong>{{ asset.name }}</strong> — {{ asset.address }} ({{
                          formatCurrency(asset.value)
                        }})
                        <br />
                        <small>{{ asset.description }}</small>
                      </li>
                    </ul>
                  </div>
                  <div v-else>Not Provided</div>
                </template>
              </InfoBlock>

              <InfoBlock label="Other Assets (Text Note)">
                <template>{{ organization.other_assets || 'Not Provided' }}</template>
              </InfoBlock>
            </SectionWrapper>

            <!-- COLLABORATIONS (EDITABLE) -->
            <SectionWrapper
              title="Collaborations"
              section="collaborations"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <InfoBlock label="Is Collaborative">
                <template v-if="editSection === 'collaborations'">
                  <select v-model="tempData.is_collaborative" class="form-select w-25 py-2">
                    <option :value="true">Yes</option>
                    <option :value="false">No</option>
                  </select>
                </template>
                <template v-else>
                  {{ organization.is_collaborative ? 'Yes' : 'No' }}
                </template>
              </InfoBlock>

              <InfoBlock label="Collaboration Partners">
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(partner, index) in editSection === 'collaborations'
                      ? tempData.collaboration_list
                      : organization.collaboration_list"
                    :key="index"
                    class="tags"
                    @click="editSection === 'collaborations' && removeCollaboration(index)"
                  >
                    {{ partner }}
                    <span v-if="editSection === 'collaborations'" class="remove-btn">×</span>
                  </span>
                </div>

                <div v-if="editSection === 'collaborations'" class="mt-2 d-flex">
                  <input
                    v-model="newCollaboration"
                    class="form-control w-50"
                    placeholder="Add new partner"
                  />
                  <button
                    class="btn btn-primary btn-sm ml-2"
                    type="button"
                    @click="addCollaboration"
                  >
                    Add
                  </button>
                </div>
              </InfoBlock>
            </SectionWrapper>

            <!-- STAFF BREAKDOWN (EDITABLE) -->
            <SectionWrapper
              title="Staff Breakdown"
              section="staff"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div v-if="currentStaff">
                <div class="staff-summary-grid">
                  <div v-for="card in staffSummaryCards" :key="card.key" class="staff-summary-card">
                    <span class="staff-summary-card__label">{{ card.label }}</span>
                    <strong class="staff-summary-card__value">{{
                      formatCountValue(card.value)
                    }}</strong>
                  </div>
                </div>

                <div class="staff-groups-grid">
                  <section
                    v-for="group in staffFieldGroups"
                    :key="group.title"
                    class="staff-group-card"
                  >
                    <div class="staff-group-card__header">
                      <p class="section-kicker">{{ group.kicker }}</p>
                      <h3>{{ group.title }}</h3>
                      <p>{{ group.description }}</p>
                    </div>

                    <div class="staff-group-card__grid">
                      <InfoBlock
                        v-for="field in group.fields"
                        :key="field.key"
                        :label="field.label"
                      >
                        <template v-if="editSection === 'staff'">
                          <input
                            v-model.number="tempData.staff_member[field.key]"
                            type="number"
                            min="0"
                            class="form-control"
                          />
                        </template>
                        <template v-else>{{ formatCountValue(currentStaff[field.key]) }}</template>
                      </InfoBlock>
                    </div>
                  </section>
                </div>
              </div>
              <div v-else class="staff-empty-state">Not Provided</div>
            </SectionWrapper>

            <!-- FILES & DOCUMENTATION -->
            <SectionWrapper
              title="Files & Documentation"
              section="files"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="true"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="Umbrella Org File (URL)">
                    <template>
                      <a
                        v-if="organization.umbrella_org_file_url"
                        :href="organization.umbrella_org_file_url"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="CAC Certificate (URL)">
                    <template>
                      <a
                        v-if="organization.cac_certificate_file_url"
                        :href="organization.cac_certificate_file_url"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Activities Documentation (URL)">
                    <template>
                      <a
                        v-if="organization.activities_documentation_file_url"
                        :href="organization.activities_documentation_file_url"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Letter of Introduction (URL)">
                    <template>
                      <a
                        v-if="organization.letter_of_introduction_url"
                        :href="organization.letter_of_introduction_url"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="File Logo (URL)">
                    <template>
                      <a
                        v-if="organization.file_logo_url"
                        :href="organization.file_logo_url"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>
                </div>

                <div class="col-md-6">
                  <!-- Legacy file fields with upload -->
                  <InfoBlock label="Umbrella Org File (Raw)">
                    <template v-if="editSection === 'files'">
                      <input type="file" @change="handleFileChange($event, 'umbrella_org_file')" />
                      <small v-if="tempFiles.umbrella_org_file"
                        >Selected: {{ tempFiles.umbrella_org_file.name }}</small
                      >
                    </template>
                    <template v-else>
                      <a
                        v-if="organization.umbrella_org_file"
                        :href="organization.umbrella_org_file"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="CAC Certificate File (Raw)">
                    <template v-if="editSection === 'files'">
                      <input
                        type="file"
                        @change="handleFileChange($event, 'cac_certificate_file')"
                      />
                      <small v-if="tempFiles.cac_certificate_file"
                        >Selected: {{ tempFiles.cac_certificate_file.name }}</small
                      >
                    </template>
                    <template v-else>
                      <a
                        v-if="organization.cac_certificate_file"
                        :href="organization.cac_certificate_file"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Activities Documentation (Raw)">
                    <template v-if="editSection === 'files'">
                      <input
                        type="file"
                        @change="handleFileChange($event, 'activities_documentation_file')"
                      />
                      <small v-if="tempFiles.activities_documentation_file"
                        >Selected: {{ tempFiles.activities_documentation_file.name }}</small
                      >
                    </template>
                    <template v-else>
                      <a
                        v-if="organization.activities_documentation_file"
                        :href="organization.activities_documentation_file"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="Letter of Introduction (Raw)">
                    <template v-if="editSection === 'files'">
                      <input
                        type="file"
                        @change="handleFileChange($event, 'letter_of_introduction')"
                      />
                      <small v-if="tempFiles.letter_of_introduction"
                        >Selected: {{ tempFiles.letter_of_introduction.name }}</small
                      >
                    </template>
                    <template v-else>
                      <a
                        v-if="organization.letter_of_introduction"
                        :href="organization.letter_of_introduction"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>

                  <InfoBlock label="File Logo (Raw)">
                    <template v-if="editSection === 'files'">
                      <input type="file" @change="handleFileChange($event, 'file_logo')" />
                      <small v-if="tempFiles.file_logo"
                        >Selected: {{ tempFiles.file_logo.name }}</small
                      >
                    </template>
                    <template v-else>
                      <a
                        v-if="organization.file_logo"
                        :href="organization.file_logo"
                        target="_blank"
                        download
                        >Download</a
                      >
                      <span v-else>None</span>
                    </template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- META INFORMATION -->
            <SectionWrapper
              title="Meta Information"
              section="meta"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="false"
            >
              <div class="row">
                <div class="col-md-6">
                  <InfoBlock label="Organization ID">
                    <template>{{ organization.id }}</template>
                  </InfoBlock>

                  <InfoBlock label="Registered On">
                    <template>{{ organization.registered_on || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Last Updated">
                    <template>{{ organization.updated_on || 'Not Provided' }}</template>
                  </InfoBlock>

                  <InfoBlock label="Reject Reason">
                    <template>{{ organization.reject_reason || 'Not Provided' }}</template>
                  </InfoBlock>
                </div>
              </div>
            </SectionWrapper>

            <!-- REPORTS (VIEW RAW) -->
            <SectionWrapper
              title="Reports"
              section="reports"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              :showEdit="false"
            >
              <InfoBlock label="Reports JSON">
                <template>
                  <div v-if="organization.reports && organization.reports.length">
                    <pre class="small bg-light p-2 rounded"
                      >{{ JSON.stringify(organization.reports, null, 2) }}
                </pre
                    >
                  </div>
                  <div v-else>Not Provided</div>
                </template>
              </InfoBlock>
            </SectionWrapper>

            <!-- ACTIONS -->
            <SectionWrapper
              title="Actions"
              section="actions"
              :editSection="editSection"
              @edit="startEdit"
              @save="saveSection"
              @cancel="cancelEdit"
              showEdit="false"
              v-if="savedContent"
            >
              <div class="d-flex justify-content-center w-100 action-row">
                <button
                  class="btn btn-sm btn-secondary w-25 py-3 action-button action-button--ghost"
                  @click="cancelOperation"
                >
                  Cancel
                </button>
                <button
                  class="btn btn-sm btn-success w-25 py-3 ml-2 action-button action-button--primary"
                  :disabled="loader"
                  @click="updateOrganization"
                >
                  <div class="spinner-border" role="status" v-if="draftLoader">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <span v-else>Update Organization</span>
                </button>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </div>

      <div v-else class="loading-shell">
        <div class="spinner-border load-icon" role="status"></div>
        <p>Loading organization details...</p>
      </div>
    </div>
  </div>
</template>

/* eslint-disable camelcase */
<script>
import {
  getOrganization,
  getProgramArea,
  updateOrganizationDetail,
} from '../../../ngo/config/api-modules/api.organization';
import SectionWrapper from './components/SectionWrapper.vue';
import InfoBlock from './components/InfoBlock.vue';

export default {
  name: 'OrganizationDetailsEditable',
  components: {
    SectionWrapper,
    InfoBlock,
  },
  data() {
    return {
      organization: null,
      tempData: {},
      tempFiles: {},
      editSection: null,
      location: [
        {
          name: 'North Central',
          states: ['BENUE', 'FCT', 'KOGI', 'KWARA', 'NASARAWA', 'NIGER', 'PLATEAU'],
        },
        {
          name: 'North East',
          states: ['ADAMAWA', 'BAUCHI', 'BORNO', 'GOMBE', 'TARABA', 'YOBE'],
        },
        {
          name: 'North West',
          states: ['JIGAWA', 'KADUNA', 'KANO', 'KATSINA', 'KEBBI', 'SOKOTO', 'ZAMFARA'],
        },
        {
          name: 'South East',
          states: ['ABIA', 'ANAMBRA', 'EBONYI', 'ENUGU', 'IMO'],
        },
        {
          name: 'South South',
          states: ['AKWA IBOM', 'BAYELSA', 'CROSS RIVER', 'DELTA', 'EDO', 'RIVERS'],
        },
        {
          name: 'South West',
          states: ['EKITI', 'LAGOS', 'OGUN', 'ONDO', 'OSUN', 'OYO'],
        },
      ],
      selectedZone: { name: null, states: [] },
      showAddFocus: false,
      programs: [],
      tempArea: [],
      savedContent: false,
      loader: false,
      draftLoader: false,
      newCollaboration: '',
    };
  },
  computed: {
    sectionLinks() {
      return [
        { id: 'general', label: 'General' },
        { id: 'contact', label: 'Contacts' },
        { id: 'basic', label: 'Basic Info' },
        { id: 'focus', label: 'Focus Areas' },
        { id: 'cac', label: 'CAC' },
        { id: 'banking', label: 'Banking' },
        { id: 'projects', label: 'Projects' },
        { id: 'assets', label: 'Assets' },
        { id: 'collaborations', label: 'Collaborations' },
        { id: 'staff', label: 'Staff' },
        { id: 'files', label: 'Files' },
        { id: 'meta', label: 'Meta' },
        { id: 'reports', label: 'Reports' },
        { id: 'actions', label: 'Actions' },
      ];
    },
    activeContacts() {
      if (this.editSection === 'contact') {
        return this.tempData?.contact_persons || [];
      }
      return this.organization?.contact_persons || [];
    },
    currentStaff() {
      if (this.editSection === 'staff') {
        return this.tempData?.staff_member || null;
      }
      return this.organization?.staff_member || null;
    },
    staffSummaryCards() {
      const staff = this.currentStaff || {};

      return [
        { key: 'total', label: 'Total Staff', value: staff.number_of_staffs },
        { key: 'senior', label: 'Senior Staff', value: staff.number_of_senior_staffs },
        { key: 'junior', label: 'Junior Staff', value: staff.number_of_junior_staffs },
        { key: 'full-time', label: 'Full-Time Staff', value: staff.number_of_full_time_staffs },
      ];
    },
    staffFieldGroups() {
      return [
        {
          kicker: 'Headcount',
          title: 'Overall Team Size',
          description: 'Core headcount totals across the organization.',
          fields: [
            { key: 'number_of_staffs', label: 'Total Staffs' },
            { key: 'number_of_senior_staffs', label: 'Senior Staffs' },
            { key: 'number_of_junior_staffs', label: 'Junior Staffs' },
          ],
        },
        {
          kicker: 'Leadership',
          title: 'Senior Staff Split',
          description: 'Gender distribution within senior staffing.',
          fields: [
            { key: 'number_of_male_senior_staffs', label: 'Male Senior Staffs' },
            { key: 'number_of_female_senior_staffs', label: 'Female Senior Staffs' },
          ],
        },
        {
          kicker: 'Operations',
          title: 'Junior Staff Split',
          description: 'Gender distribution within junior staffing.',
          fields: [
            { key: 'number_of_male_junior_staffs', label: 'Male Junior Staffs' },
            { key: 'number_of_female_junior_staffs', label: 'Female Junior Staffs' },
          ],
        },
        {
          kicker: 'Capacity',
          title: 'Employment Type',
          description: 'Staff allocation by working arrangement.',
          fields: [
            { key: 'number_of_full_time_staffs', label: 'Full-Time Staffs' },
            { key: 'number_of_part_time_staffs', label: 'Part-Time Staffs' },
          ],
        },
      ];
    },
  },
  methods: {
    getListValues(value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      let normalizedValue = value;

      if (typeof normalizedValue === 'string') {
        const trimmedValue = normalizedValue.trim();

        if (!trimmedValue) {
          return [];
        }

        try {
          normalizedValue = JSON.parse(trimmedValue);
        } catch (error) {
          return [trimmedValue];
        }
      }

      if (Array.isArray(normalizedValue)) {
        return normalizedValue
          .map((item) => {
            if (item === null || item === undefined) {
              return '';
            }

            if (typeof item === 'object') {
              return item.name || item.label || item.title || String(item.id || '').trim();
            }

            return String(item).trim();
          })
          .filter(Boolean);
      }

      if (typeof normalizedValue === 'object') {
        return [
          normalizedValue.name ||
            normalizedValue.label ||
            normalizedValue.title ||
            String(normalizedValue.id || '').trim(),
        ].filter(Boolean);
      }

      return [String(normalizedValue).trim()].filter(Boolean);
    },
    formatListDisplay(value) {
      const listValues = this.getListValues(value);
      return listValues.length ? listValues.join(', ') : 'Not Provided';
    },
    formatCountValue(value) {
      return value === 0 || value ? value : 'Not Provided';
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return 'Not available';

      const normalizedValue = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
      const amount = Number(normalizedValue);

      if (Number.isNaN(amount)) return value;

      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(amount);
    },
    formatDisplayDate(value) {
      if (!value) return 'Not available';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Not available';

      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    goBack() {
      localStorage.setItem('from', 'ApprovedDetailsPage');
      this.$router.go(-1);
    },
    cancelOperation() {
      this.savedContent = false;
      this.tempData = JSON.parse(JSON.stringify(this.organization));
      this.editSection = null;
    },

    addContact() {
      if (!Array.isArray(this.tempData.contact_persons)) {
        this.$set(this.tempData, 'contact_persons', []);
      }
      this.tempData.contact_persons.push({
        id: Date.now(),
        full_name: '',
        email: '',
        phone_number: '',
        address: '',
        position: '',
        cv: null,
        cv_url: null,
      });
    },

    removeContact(index) {
      if (this.tempData.contact_persons && this.tempData.contact_persons.length > index) {
        this.tempData.contact_persons.splice(index, 1);
      }
    },

    addCollaboration() {
      const value = this.newCollaboration && this.newCollaboration.trim();
      if (!value) return;
      if (!Array.isArray(this.tempData.collaboration_list)) {
        this.$set(this.tempData, 'collaboration_list', []);
      }
      this.tempData.collaboration_list.push(value);
      this.newCollaboration = '';
    },

    removeCollaboration(index) {
      if (this.tempData.collaboration_list && this.tempData.collaboration_list.length > index) {
        this.tempData.collaboration_list.splice(index, 1);
      }
    },

    async getOrganizationByID() {
      const { id } = this.$route.params;

      try {
        const { data } = await getOrganization(id);
        this.organization = data.data;

        // Ensure user object exists
        this.organization.user = this.organization.user || { email: '' };

        // Ensure arrays exist
        this.organization.areas_of_focus = Array.isArray(this.organization.areas_of_focus)
          ? this.organization.areas_of_focus
          : [];
        this.organization.contact_persons = Array.isArray(this.organization.contact_persons)
          ? this.organization.contact_persons.map((c, index) => ({
              id: c.id ?? Date.now() + index,
              ...c,
            }))
          : [];
        this.organization.collaboration_list = Array.isArray(this.organization.collaboration_list)
          ? this.organization.collaboration_list
          : [];

        // staff_member default
        if (!this.organization.staff_member) {
          this.organization.staff_member = null;
        }

        // Pre-select Geo Zone from organization.geo_zone if possible
        const zone = this.location.find((z) => z.name === this.organization.geo_zone);
        this.selectedZone = zone || { name: null, states: [] };

        // Deep clone to tempData
        this.tempData = JSON.parse(JSON.stringify(this.organization));
      } catch (e) {
        console.error(e);

        // fallback when request fails
        this.organization = {
          email: '',
          web_url: '',
          state: '',
          geo_zone: '',
          address: '',
          user: { email: '' },
          areas_of_focus: [],
          contact_persons: [],
          collaboration_list: [],
        };

        this.tempData = JSON.parse(JSON.stringify(this.organization));
      }
    },

    isValidContact(contact) {
      return contact.full_name?.trim() && contact.email?.trim();
    },

    validateContacts(contacts) {
      if (!Array.isArray(contacts)) return false;
      return contacts.every(this.isValidContact);
    },

    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => {
          if (value === '' || value === null || value === 'Select One' || value === '0') {
            return false;
          }
          if (Array.isArray(value) && value.length === 0) return false;
          if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0
          ) {
            return false;
          }
          return true;
        })
      );
    },

    async updateOrganization() {
      const contacts = this.organization.contact_persons || [];

      if (!this.validateContacts(contacts)) {
        this.$toast.show({
          type: 'Error',
          message: `Each contact person must include full name and email.`,
        });
        return;
      }

      this.loader = true;

      const updatedOrganization = {
        ...this.organization,
        // eslint-disable-next-line camelcase
        contact_persons: this.organization.contact_persons.map(({ id, cv_url, ...rest }) => rest),
      };

      const cleaned = this.cleanObject(updatedOrganization);

      // Areas of focus: send IDs only
      const tempAreaIds = Array.isArray(this.organization.areas_of_focus)
        ? this.organization.areas_of_focus.map((area) => area.id).filter(Boolean)
        : [];
      cleaned.areas_of_focus = tempAreaIds;

      console.log(cleaned, '@@@');

      updateOrganizationDetail(cleaned)
        .then((e) => {
          console.log(e);
          this.$toast.show({
            type: 'SUCCESS',
            message: `${cleaned.name} has been updated successfully`,
          });
          this.$router.push('/admin/organization');
        })
        .catch(() => {
          this.$toast.show({
            type: 'ERROR',
            message: `Unable to update ${cleaned.name}`,
          });
        })
        .finally(() => {
          this.loader = false;
        });
    },

    toggleAddFocus() {
      this.showAddFocus = !this.showAddFocus;
    },
    addFocus() {
      if (this.tempArea && this.tempArea.length > 0) {
        if (!Array.isArray(this.tempData.areas_of_focus)) {
          this.$set(this.tempData, 'areas_of_focus', []);
        }
        this.tempData.areas_of_focus.push(...this.tempArea);
        this.tempArea = [];
        this.showAddFocus = false;
      }
    },
    removeFocus(index) {
      this.tempData.areas_of_focus.splice(index, 1);
    },

    async getPrograms() {
      this.program_area_loader = true;
      try {
        const data = await getProgramArea();
        this.total = data.count;
        this.programs = data.results;
      } catch (error) {
        this.programs = [];
      } finally {
        this.program_area_loader = false;
      }
    },

    startEdit(section) {
      // fresh deep copy
      this.tempData = JSON.parse(JSON.stringify(this.organization));
      this.tempData.user = this.tempData.user || { email: '' };
      this.tempData.areas_of_focus = this.tempData.areas_of_focus || [];
      this.tempData.collaboration_list = this.tempData.collaboration_list || [];

      if (section === 'staff') {
        if (!this.tempData.staff_member) {
          this.tempData.staff_member = {
            number_of_staffs: 0,
            number_of_senior_staffs: 0,
            number_of_junior_staffs: 0,
            number_of_male_senior_staffs: 0,
            number_of_female_senior_staffs: 0,
            number_of_male_junior_staffs: 0,
            number_of_female_junior_staffs: 0,
            number_of_full_time_staffs: 0,
            number_of_part_time_staffs: 0,
          };
        }
      }

      if (section === 'general') {
        const zone = this.location.find((z) => z.name === this.tempData.geo_zone);
        this.selectedZone = zone || { name: null, states: [] };
      }

      if (section !== 'files') this.tempFiles = {};
      this.editSection = section;
    },

    saveSection() {
      // Handle geo_zone from selectedZone for general section
      if (this.editSection === 'general' && this.selectedZone && this.selectedZone.name) {
        this.tempData.geo_zone = this.selectedZone.name;
      }

      // merge tempData into organization
      this.organization = { ...this.organization, ...this.tempData };
      this.savedContent = true;

      if (this.editSection === 'files' && this.tempFiles) {
        Object.keys(this.tempFiles).forEach((field) => {
          const file = this.tempFiles[field];
          if (file) {
            const previewUrl = URL.createObjectURL(file);
            this.$set(this.organization, field, previewUrl);
          }
        });
      }

      this.editSection = null;
    },

    cancelEdit() {
      this.tempData = JSON.parse(JSON.stringify(this.organization));
      this.tempFiles = {};
      this.editSection = null;
    },

    handleFileChange(event, field) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.$set(this.tempFiles, field, file);
    },
  },

  async mounted() {
    await this.getOrganizationByID();
    await this.getPrograms();
  },
};
</script>

<style lang="scss" scoped>
.organization-details-page {
  min-height: 100vh;
  padding: 1rem 0 2.5rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.14), transparent 24%),
    linear-gradient(180deg, #f5faf6 0%, #eef5ef 100%);
}

.message-wrapper {
  max-width: 1320px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  padding: 0.85rem 1.15rem;
  margin-bottom: 1.25rem;
  border: 0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 14px 32px rgba(18, 43, 25, 0.08);
  color: #1f3826;
  font-weight: 700;
  width: fit-content;
}

.details-shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.details-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(10, 33, 17, 0.96), rgba(23, 72, 36, 0.88)),
    linear-gradient(140deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 28px 80px rgba(17, 41, 24, 0.16);
  color: #f7fff7;
}

.hero-main {
  max-width: 48rem;
}

.hero-eyebrow,
.section-kicker {
  margin-bottom: 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-eyebrow {
  color: rgba(247, 255, 247, 0.72);
}

.details-hero h1 {
  margin-bottom: 0.85rem;
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.08;
}

.hero-subtitle {
  margin-bottom: 1.2rem;
  max-width: 42rem;
  color: rgba(247, 255, 247, 0.82);
  line-height: 1.75;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.hero-pill,
.hero-meta-text {
  display: inline-flex;
  align-items: center;
  min-height: 2.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
}

.hero-pill {
  font-weight: 700;
}

.hero-pill--success {
  background: rgba(232, 245, 235, 0.18);
  color: #d9ffe1;
}

.hero-pill--pending {
  background: rgba(255, 243, 205, 0.16);
  color: #fff1bb;
}

.hero-meta-text {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(247, 255, 247, 0.82);
  font-size: 0.9rem;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(1, minmax(180px, 1fr));
  gap: 1rem;
  min-width: 16rem;
}

.hero-stat {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.hero-stat__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.hero-stat__value {
  font-size: 1.35rem;
  line-height: 1.3;
}

.hero-stat__value--small {
  font-size: 1rem;
}

.details-card {
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 18px 60px rgba(22, 50, 28, 0.08);
  backdrop-filter: blur(10px);
}

.section-nav {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.2rem 1.35rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 40px rgba(22, 50, 28, 0.06);
}

.section-nav__copy h3 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-size: 1.15rem;
  color: #14261a;
}

.section-nav__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.6rem;
}

.section-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2.4rem;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-size: 0.84rem;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.section-chip:hover {
  transform: translateY(-1px);
  background: #e3f2e7;
  box-shadow: 0 10px 20px rgba(48, 171, 76, 0.12);
}

.details-card__header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-end;
  padding: 1.75rem 1.75rem 0;
}

.section-kicker {
  color: #2c8d41;
}

.details-card__header h2 {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  color: #132218;
}

.details-card__note {
  max-width: 23rem;
  margin: 0;
  color: #5e6d64;
  line-height: 1.6;
  text-align: right;
}

.premium-card-body {
  padding: 1.2rem 1.75rem 1.75rem;
}

.premium-card-body ::v-deep .section-wrapper {
  padding: 1.4rem 1.35rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.96), rgba(241, 247, 242, 0.96));
  box-shadow: 0 10px 28px rgba(18, 42, 24, 0.05);
}

.premium-card-body ::v-deep .section-header {
  padding: 0 0 1rem;
  margin-bottom: 1.1rem;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
}

.premium-card-body ::v-deep .section-title {
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-size: 1.05rem;
  color: #14261a;
  border-bottom: 0;
}

.premium-card-body ::v-deep .section-header .btn {
  min-width: 5.8rem;
  border: 0;
  border-radius: 12px;
  font-weight: 700;
}

.premium-card-body ::v-deep .section-header .btn-primary,
.premium-card-body ::v-deep .section-header .btn-success {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 12px 24px rgba(48, 171, 76, 0.16);
}

.premium-card-body ::v-deep .section-header .btn-secondary {
  background: #e9efeb;
  color: #223429;
}

.premium-card-body ::v-deep .info-block {
  margin-bottom: 1.05rem;
}

.premium-card-body ::v-deep .info-label,
.premium-card-body label {
  display: block;
  margin-bottom: 0.38rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #68786f;
}

.premium-card-body ::v-deep .info-value {
  color: #213228;
  line-height: 1.65;
  word-break: break-word;
}

.staff-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.staff-summary-card {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(246, 250, 247, 0.98), rgba(237, 245, 239, 0.92));
  box-shadow: 0 14px 28px rgba(18, 42, 24, 0.05);
}

.staff-summary-card__label {
  display: block;
  margin-bottom: 0.5rem;
  color: #627168;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.staff-summary-card__value {
  color: #173a21;
  font-size: 1.9rem;
  line-height: 1;
}

.staff-groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.staff-group-card {
  padding: 1.15rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 16px 30px rgba(18, 42, 24, 0.04);
}

.staff-group-card__header {
  margin-bottom: 1rem;
}

.staff-group-card__header h3 {
  margin: 0 0 0.35rem;
  color: #183523;
  font-size: 1.05rem;
}

.staff-group-card__header p:last-child {
  margin: 0;
  color: #607066;
  font-size: 0.95rem;
}

.staff-group-card__grid {
  display: grid;
  gap: 0.9rem;
}

.staff-empty-state {
  padding: 1.1rem 1.2rem;
  border: 1px dashed rgba(28, 68, 38, 0.16);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.65);
  color: #607066;
  text-align: center;
}

.large-text-display {
  min-height: 6.2rem;
  display: flex;
  align-items: flex-start;
  white-space: pre-wrap;
}

.premium-card-body input.form-control,
.premium-card-body textarea.form-control,
.premium-card-body select.form-select,
.premium-card-body input[type='file'] {
  min-height: 3.1rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
  color: #1f3225;
}

.premium-card-body textarea.form-control {
  min-height: 6.2rem;
  padding-top: 0.9rem;
}

.premium-card-body input.form-control:focus,
.premium-card-body textarea.form-control:focus,
.premium-card-body select.form-select:focus {
  border-color: rgba(48, 171, 76, 0.6);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.1);
}

.premium-card-body ::v-deep .multiselect {
  min-height: 3.1rem;
}

.premium-card-body ::v-deep .multiselect__tags {
  min-height: 3.1rem;
  border: 1px solid rgba(28, 68, 38, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 20px rgba(18, 42, 24, 0.04);
}

.premium-card-body ::v-deep .multiselect__tag,
.tags {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  background: #edf7ef;
  color: #256536;
  font-weight: 600;
}

.tag-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-left: 0.35rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  color: #fff;
}

.remove-btn {
  position: static;
  font-size: 1rem;
  color: #c34b4b;
}

.action-row {
  gap: 0.85rem;
}

.action-button {
  min-width: 12rem;
  border: 0;
  border-radius: 16px;
  font-weight: 700;
}

.action-button--ghost {
  background: #e9efeb;
  color: #223429;
}

.action-button--primary {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
}

.loading-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 18rem;
  text-align: center;
}

.loading-shell p {
  margin-top: 1rem;
  color: #5f6f66;
}

.load-icon {
  width: 4rem;
  height: 4rem;
  color: #2da047;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1rem;
  border: 1px solid rgba(28, 68, 38, 0.08);
  border-radius: 18px;
  background: #f7faf7 !important;
}

@media (max-width: 991px) {
  .organization-details-page {
    padding-top: 0rem;
  }

  .details-hero,
  .details-card__header,
  .section-nav {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-stats {
    width: 100%;
    min-width: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .details-card__note {
    text-align: left;
  }

  .section-nav__chips {
    justify-content: flex-start;
  }
}

@media (max-width: 767px) {
  .organization-details-page {
    padding: 0rem 0 2rem;
  }

  .details-hero,
  .details-card {
    border-radius: 22px;
  }

  .details-hero,
  .premium-card-body,
  .details-card__header {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .hero-stats {
    grid-template-columns: 1fr;
  }

  .action-row {
    flex-direction: column;
  }

  .action-button,
  .premium-card-body .w-25,
  .premium-card-body .w-50 {
    width: 100% !important;
  }
}

@media (max-width: 600px) {
  .organization-details-page {
    padding-top: 0rem;
  }
}
</style>
